import {
  type ADDITIONAL_CLEARANCE_TIERS,
  type CountryCode,
  type CustomerOrder,
  type Scope,
  type ScriptCity,
  type ScriptProductionType,
  type ScriptProject,
  scriptDraftOptionsSchema,
  type TimePeriod,
  type TurnaroundDays,
  type VERSION_PACKS,
} from '@noconflict/contracts';
import { useCallback, useRef, useState } from 'react';
import { orderErrorMessage, useUpdateOrderDraft } from '../../../entities/order';
import { useUpdateScriptProject } from '../../../entities/script-project';

export type ClearanceTier = (typeof ADDITIONAL_CLEARANCE_TIERS)[number];
export type VersionPack = (typeof VERSION_PACKS)[number];

/** A city as the form holds it: the country may still be unchosen while the row is being filled in. */
export type CityDraft = { name: string; country: CountryCode | null };

/** What the form holds: text exactly as typed, choices as chosen (a scope is null until one is). */
export type ScriptDraftValues = {
  productionType: ScriptProductionType | null;
  title: string;
  episodeTitle: string;
  episodeNumber: string;
  producer: string;
  /** Writers as typed, separated by commas. */
  authors: string;
  timePeriods: TimePeriod[];
  countries: CountryCode[];
  noSpecificCountry: boolean;
  cities: CityDraft[];
  basedOnReal: boolean | null;
  basedOnRealNote: string;
  notes: string;
  scope: Scope | null;
  turnaroundDays: TurnaroundDays;
  additionalClearances: ClearanceTier;
  versions: VersionPack;
  reference: string;
};

export type ScriptMissingField = 'title' | 'productionType' | 'scope' | 'reference';

export type ScriptDraft = {
  values: ScriptDraftValues;
  /** Changes a value on screen only; `save` (on blur) persists it. */
  set: (patch: Partial<ScriptDraftValues>) => void;
  /** Changes a value and saves at once: selects, checkboxes and radio cards. */
  commit: (patch: Partial<ScriptDraftValues>) => void;
  /** Saves whatever differs from what was last saved. */
  save: () => void;
  /** Resolves once every pending save has finished; false when the last one failed. */
  flush: () => Promise<boolean>;
  status: 'idle' | 'saving' | 'saved' | 'error';
  error: string | null;
  /** What a submit still needs. */
  missing: ScriptMissingField[];
};

const authorsOf = (text: string) =>
  text
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);

/** Only a city with a name and a country is saved; a half-filled row waits on screen. */
const citiesOf = (cities: CityDraft[]): ScriptCity[] =>
  cities.flatMap((city) =>
    city.name.trim() && city.country ? [{ name: city.name.trim(), country: city.country }] : [],
  );

const detailsInput = (v: ScriptDraftValues) => ({
  productionType: v.productionType,
  title: v.title.trim() || null,
  // an episode's own title and number belong to a TV episode only
  episodeTitle: v.productionType === 'tv_episode' ? v.episodeTitle.trim() || null : null,
  episodeNumber: v.productionType === 'tv_episode' ? v.episodeNumber.trim() || null : null,
  producer: v.producer.trim() || null,
  authors: authorsOf(v.authors),
  timePeriods: v.timePeriods,
  countries: v.noSpecificCountry ? [] : v.countries,
  noSpecificCountry: v.noSpecificCountry,
  cities: citiesOf(v.cities),
  basedOnReal: v.basedOnReal,
  basedOnRealNote: v.basedOnReal ? v.basedOnRealNote.trim() || null : null,
  notes: v.notes.trim() || null,
});
const detailsOf = (v: ScriptDraftValues) => JSON.stringify(detailsInput(v));
const orderOf = (v: ScriptDraftValues) =>
  JSON.stringify([
    v.scope,
    v.turnaroundDays,
    v.additionalClearances,
    v.versions,
    v.reference.trim(),
  ]);

/**
 * The state of the script order form. Every field saves by itself (text when the field is left, a choice when it
 * is made): details through `update_script_project`, the priced choices and the reference through the order
 * draft. Saves are queued, so they reach the database in the order they were made. The script's length is not
 * here at all: it comes from the analysis, and the database ignores whatever a draft says about it.
 */
export function useScriptDraft({
  projectId,
  details,
  order,
  referenceRequired,
}: {
  projectId: string;
  details: ScriptProject;
  order: CustomerOrder;
  referenceRequired: boolean;
}): ScriptDraft {
  const saveDetails = useUpdateScriptProject(projectId);
  const saveOrder = useUpdateOrderDraft();
  const [values, setValues] = useState<ScriptDraftValues>(() => {
    const options = scriptDraftOptionsSchema.safeParse(order.options).data;
    return {
      productionType: details.production_type,
      title: details.title ?? '',
      episodeTitle: details.episode_title ?? '',
      episodeNumber: details.episode_number ?? '',
      producer: details.producer ?? '',
      authors: details.authors.join(', '),
      timePeriods: details.time_periods,
      countries: details.countries,
      noSpecificCountry: details.no_specific_country,
      cities: details.cities,
      basedOnReal: details.based_on_real,
      basedOnRealNote: details.based_on_real_note ?? '',
      notes: details.notes ?? '',
      scope: options?.scope ?? null,
      turnaroundDays: options?.turnaroundDays ?? 5,
      additionalClearances: options?.additionalClearances ?? 5,
      versions: options?.versions ?? 0,
      reference: order.reference ?? '',
    };
  });
  const [status, setStatus] = useState<ScriptDraft['status']>('idle');
  const [error, setError] = useState<string | null>(null);
  // The latest values and what the database holds, outside React's render cycle: a save reads them when it runs.
  const latest = useRef(values);
  const saved = useRef({ details: detailsOf(values), order: orderOf(values) });
  const queue = useRef<Promise<boolean>>(Promise.resolve(true));
  const { mutateAsync: mutateDetails } = saveDetails;
  const { mutateAsync: mutateOrder } = saveOrder;
  const orderId = order.id;

  const persist = useCallback(async (): Promise<boolean> => {
    const v = latest.current;
    const next = { details: detailsOf(v), order: orderOf(v) };
    if (next.details === saved.current.details && next.order === saved.current.order) return true;
    setStatus('saving');
    setError(null);
    try {
      if (next.details !== saved.current.details) {
        await mutateDetails(detailsInput(v));
        saved.current.details = next.details;
      }
      if (next.order !== saved.current.order) {
        await mutateOrder({
          orderId,
          draft: {
            product: 'script',
            options: {
              // never the customer's to set: the database writes the analysis's length over this
              length: null,
              scope: v.scope,
              turnaroundDays: v.turnaroundDays,
              additionalClearances: v.additionalClearances,
              versions: v.versions,
            },
            reference: v.reference.trim() || null,
          },
        });
        saved.current.order = next.order;
      }
      setStatus('saved');
      return true;
    } catch (e) {
      setStatus('error');
      setError(orderErrorMessage(e));
      return false;
    }
  }, [mutateDetails, mutateOrder, orderId]);

  const save = useCallback(() => {
    queue.current = queue.current.then(persist, persist);
  }, [persist]);

  const set = useCallback((patch: Partial<ScriptDraftValues>) => {
    latest.current = { ...latest.current, ...patch };
    setValues(latest.current);
  }, []);

  const commit = useCallback(
    (patch: Partial<ScriptDraftValues>) => {
      set(patch);
      save();
    },
    [set, save],
  );

  const flush = useCallback(() => {
    save();
    return queue.current;
  }, [save]);

  const missing: ScriptMissingField[] = [];
  if (values.title.trim() === '') missing.push('title');
  if (values.productionType === null) missing.push('productionType');
  if (values.scope === null) missing.push('scope');
  if (referenceRequired && values.reference.trim() === '') missing.push('reference');

  return { values, set, commit, save, flush, status, error, missing };
}
